import ajax from '@ep/tools/ajax.js';
import storage from '@ep/tools/storage';
import session from '@ep/tools/session';
import toast from '@ep/tools/toast';
import msgbox from '@ep/tools/msgbox';
import authClient from '@ep/auth-client/client';
import { inferProductFromHost } from '@ep/config/domainMap';
import { resolveKnownImageSource } from '@ep/components/create/image';
import createElement from '@ep/components/create/element';
import {
  applyVisualPreferences,
  normalizeVisualScale,
  normalizeVisualTheme,
  readVisualPreferences,
} from '@ep/tools/visualPreferences';

const DEFAULT_MENU_DICS = ['categories', 'domains|menu', 'modules|menu'];
const AUTH_STATE_STORAGE_KEY = 'ep:ecosystem-auth-state';
const markRecentSsoProbe = () => {
  if (typeof window === 'undefined') return;
  const currentProduct = inferProductFromHost(window.location?.hostname || '') || 'platform';
  try {
    window.sessionStorage.setItem(`ep:sso-probe:${currentProduct}`, String(Date.now()));
  } catch {}
};

const markEcosystemUnauthenticated = () => {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(AUTH_STATE_STORAGE_KEY, JSON.stringify({
      state: 'unauthenticated',
      timestamp: Date.now(),
    }));
  } catch {}
};

const resolvePrivateFileUrl = (value) => {
  const raw = String(value || '').trim();
  if (!raw || raw === 'null' || raw === 'undefined') return '';
  if (/^https?:\/\//i.test(raw) || raw.startsWith('/') || raw.startsWith('@')) return raw;
  return `/private-files/${raw.replace(/^\/+/, '').split('/').map(encodeURIComponent).join('/')}`;
};

const versionPrivateFileUrl = (value) => {
  const url = resolvePrivateFileUrl(value);
  if (!url) return '';
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}v=${Date.now()}`;
};

export class LoggedShellInit {
  get config() {
    const value = this.#globalValue('config');
    return value && typeof value === 'object' ? value : {};
  }

  get dic() {
    const value = this.#globalValue('dic');
    return value && typeof value === 'object' ? value : {};
  }

  async run() {
    const routeSlug = this.#normalizeSlug(session.get('routeSlug'));
    session.delete('routeSlug');

    const initialTitle = String(this.dic?.branding?.seo?.title || this.#productTitle()).trim();
    if (initialTitle) {
      document.title = initialTitle;
    }

    await this.#render(routeSlug, { forceRefreshMenu: true });
  }

  #globalValue(name) {
    if (typeof window !== 'undefined' && window?.[name]) return window[name];
    if (typeof globalThis !== 'undefined' && globalThis?.[name]) return globalThis[name];
    return null;
  }

  #label(key, fallback = '') {
    const labels = this.dic?.labels;
    const value = labels && typeof labels === 'object' ? labels[key] : null;
    return typeof value === 'string' && value.trim() ? value : fallback;
  }

  #format(value, params = {}) {
    return String(value || '').replace(/\{([^}]+)\}/g, (_, key) => {
      const replacement = params[key];
      return replacement === undefined || replacement === null ? '' : String(replacement);
    });
  }

  #refreshProfileHeader() {
    const trigger = document.querySelector('.user-menu-trigger');
    if (!trigger) return;

    const userName = session.get('user') || '';
    const greeting = trigger.querySelector('span:first-child');
    if (greeting) {
      greeting.textContent = this.#format(this.#label('hello', 'Ola, {name}!'), { name: userName });
    }

    const avatar = trigger.querySelector('img.header-avatar');
    const userPhotoURL = session.get('userPhotoURL');
    if (avatar && userPhotoURL) avatar.src = userPhotoURL;
  }

  #normalizeSlug(rawSlug) {
    if (typeof rawSlug !== 'string') return null;

    const slug = rawSlug
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase()
      .replace(/^\/+|\/+$/g, '');

    if (!slug || slug === 'null' || slug === 'undefined') return null;
    return slug;
  }

  #updateUrl(slug, { replace = false } = {}) {
    const normalized = this.#normalizeSlug(slug);
    const path = normalized ? `/${normalized}` : '/';

    if (window.location.pathname === path) return;
    window.history[replace ? 'replaceState' : 'pushState']({}, '', path);
  }

  #productTitle() {
    const branding = this.dic?.branding || {};
    return branding.productName
      || branding.seo?.title
      || this.config.logoAlt
      || this.config.product
      || 'App';
  }

  #updateTitle(sectionLabel) {
    document.title = `${this.#productTitle()} | ${sectionLabel || 'Area Logada'}`;
  }

  #trackPageView() {
    const payload = {
      page_title: document.title,
      page_path: window.location.pathname || '/',
      page_location: window.location.href
    };

    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', payload);
    }

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push({ event: 'page_view', ...payload });
    }
  }

  async #logout() {
    const persistedMfaDeviceId = storage.get('mfaDeviceId');
    const currentProduct = inferProductFromHost(window.location?.hostname || '') || 'platform';
    try {
      await authClient.logoutCurrent(
        { deviceId: persistedMfaDeviceId || null },
        { loader: false }
      );
    } catch (error) {
      console.error('Erro ao executar logout:', error);
    }

    try {
      await ajax.post({
        product: currentProduct,
        category: 'core',
        domain: 'core',
        module: 'master',
        method: 'logoutLocalSession',
        body: {},
        config: { loader: false, retries: 1, retryDelay: 100 }
      });
    } catch (error) {
      console.error('Erro ao encerrar sessão local:', error);
    } finally {
      session.clear();
      storage.clear();
      markEcosystemUnauthenticated();
      if (persistedMfaDeviceId) {
        storage.set('mfaDeviceId', persistedMfaDeviceId);
      }
      markRecentSsoProbe();
      window.location.replace('/');
    }
  }

  async #refreshMenu() {
    try {
      const currentProduct = inferProductFromHost(window.location?.hostname || '') || 'platform';
      const sessionData = currentProduct && currentProduct !== 'site'
        ? await ajax.get({
          product: currentProduct,
          category: 'core',
          domain: 'core',
          module: 'master',
          method: 'getSessionData',
          query: { refresh: '1' },
          config: { loader: false, retries: 1, retryDelay: 100 },
        })
        : await authClient.getSessionData({ loader: false, forceRefresh: true });
      const profile = sessionData?.profile || {};
      const nickname = String(profile?.nickname || '').trim();
      if (nickname) {
        session.set('user', nickname);
      }
      if (profile?.photoURL) {
        session.set('userPhotoURL', resolvePrivateFileUrl(profile.photoURL));
      }
      if (sessionData?.token) {
        session.set('token', sessionData.token);
      }

      const refreshedMenu = await authClient.getMenu({ loader: false, forceRefresh: true });
      if (!Array.isArray(refreshedMenu)) {
        throw new Error('MENU_REFRESH_INVALID_RESPONSE');
      }

      const currentSlug = this.#normalizeSlug(window.location.pathname);
      await this.#render(currentSlug, {
        menu: refreshedMenu,
      });

      toast({
        type: 'success',
        message: this.#label('updateMenuSuccess', 'Menu atualizado.')
      });
    } catch (error) {
      console.error('Erro ao atualizar menu:', error);
      toast({
        type: 'error',
        message: this.#label('updateMenuError', 'Nao foi possível atualizar o menu.')
      });
    }
  }

  #isStrongPassword(password = '') {
    const value = String(password || '');
    if (value.length < 8) return false;
    if (!/[A-Z]/.test(value)) return false;
    if (!/[a-z]/.test(value)) return false;
    if (!/[0-9]/.test(value)) return false;
    if (!/[^A-Za-z0-9]/.test(value)) return false;
    return true;
  }

  #loginLabel(key, fallback = '') {
    const login = this.dic?.login;
    const value = login && typeof login === 'object' ? login[key] : null;
    return typeof value === 'string' && value.trim() ? value : fallback;
  }

  #changePasswordMessage(response = {}) {
    const status = Number(response?.status || 0);
    const error = String(response?.error || '').trim();

    if (status === 401 && error === 'CURRENT_PASSWORD_INVALID') {
      return this.#loginLabel('currentPasswordInvalid', 'Senha atual incorreta.');
    }
    if (status === 401) {
      return this.#loginLabel('sessionExpired', 'Sua sessao expirou. Entre novamente.');
    }
    if (status === 409 && error === 'PASSWORD_NOT_SET') {
      return this.#loginLabel('passwordNotSet', 'Sua conta ainda nao possui senha. Use o fluxo de recuperacao.');
    }
    if (status === 422 && error === 'PASSWORD_REUSED') {
      return this.#loginLabel('passwordReused', 'Voce nao pode reutilizar a senha anterior.');
    }
    if (status === 422) {
      return this.#loginLabel('passwordStrongPolicy', 'Senha fora da politica minima de seguranca.');
    }

    return this.dic?.message?.errorOperation || 'Nao foi possivel alterar a senha.';
  }

  async #openChangePasswordDialog() {
    this.#closeUserMenu();

    await create({
      element: 'div',
      parent: document.body,
      class: 'change-password-panel',
      dialog: {
        modal: true,
        effect: 'zoom',
        autoOpen: true,
        kill: true,
        width: 420,
        class: 'change-password-dialog',
        title: {
          icon: { type: 'solid', name: 'shield-halved' },
          text: this.#loginLabel('changePasswordTitle', this.#label('changePassword', 'Alterar Senha'))
        },
        buttons: [
          {
            text: this.#label('cancel', 'Cancelar'),
            color: 'red',
            icon: { type: 'solid', name: 'ban' }
          },
          {
            text: this.#label('save', 'Salvar'),
            color: 'green',
            icon: { type: 'solid', name: 'floppy-disk' },
            data: { button: 'enter' },
            close: false,
            click: (event) => {
              const button = event.currentTarget;
              const dialog = button.closest('.dialog');
              const currentPasswordInput = dialog?.querySelector('input[name="currentPassword"]');
              const newPasswordInput = dialog?.querySelector('input[name="newPassword"]');
              const confirmPasswordInput = dialog?.querySelector('input[name="confirmPassword"]');

              const currentPassword = String(currentPasswordInput?.value || '');
              const newPassword = String(newPasswordInput?.value || '');
              const confirmPassword = String(confirmPasswordInput?.value || '');

              if (!currentPassword || !newPassword || !confirmPassword) {
                msgbox({ type: 'warning', message: this.dic?.message?.fillInAllFields || 'Preencha todos os campos.' });
                return;
              }

              if (newPassword !== confirmPassword) {
                msgbox({ type: 'warning', message: this.#loginLabel('passwordMismatch', 'As senhas nao conferem.') });
                return;
              }

              if (!this.#isStrongPassword(newPassword)) {
                msgbox({ type: 'warning', message: this.#loginLabel('passwordStrongPolicy', 'Senha fora da politica minima de seguranca.') });
                return;
              }

              (async () => {
                button.disabled = true;
                try {
                  const response = await authClient.changePassword({
                    currentPassword,
                    newPassword
                  });

                  if (response?.status === 200) {
                    dialog?.instance?.close?.('destroy');
                    toast({
                      type: 'success',
                      message: this.#loginLabel('changePasswordSuccess', 'Senha alterada com sucesso.')
                    });
                    return;
                  }

                  msgbox({
                    type: 'warning',
                    message: this.#changePasswordMessage(response)
                  });
                } catch (error) {
                  console.error('changePassword error:', error);
                  msgbox({
                    type: 'error',
                    message: this.dic?.message?.errorOperation || 'Nao foi possivel alterar a senha.'
                  });
                } finally {
                  button.disabled = false;
                }
              })();
            }
          }
        ]
      },
      isGrid: true,
      content: [
        {
          element: 'div',
          class: 'change-password-intro',
          html: this.#loginLabel(
            'changePasswordIntro',
            'Confirme sua senha atual e escolha uma nova senha forte.'
          )
        },
        {
          element: 'password',
          id: 'current-password',
          name: 'currentPassword',
          label: this.#loginLabel('currentPassword', 'Senha atual'),
          full: true,
          view: true
        },
        {
          element: 'password',
          id: 'change-new-password',
          name: 'newPassword',
          label: this.#loginLabel('newPassword', 'Nova senha'),
          full: true,
          view: true,
          strong: true
        },
        {
          element: 'password',
          id: 'change-confirm-password',
          name: 'confirmPassword',
          label: this.#loginLabel('confirmNewPassword', 'Confirmar senha'),
          full: true,
          view: true
        }
      ]
    });
  }

  async #getCompanyProfileAccess() {
    try {
      const response = await authClient.callAuthMethod('getCompanyProfile', {}, {
        loader: false,
        retries: 1,
        retryDelay: 100,
      });
      return {
        allowed: response?.allowed === true,
        company: response?.company || null,
      };
    } catch (error) {
      console.warn('getCompanyProfile access error:', error?.message || error);
      return { allowed: false, company: null };
    }
  }

  async #openCompanyProfileDialog(companyAccess = null) {
    this.#closeUserMenu();

    let access = companyAccess;
    if (!access?.allowed) {
      access = await this.#getCompanyProfileAccess();
    }
    if (!access?.allowed || !access?.company) {
      return;
    }

    const company = access.company;
    const data = {
      company_name: company.company_name || '',
      trade_name: company.trade_name || '',
      document_country: company.document_country || 'BR',
      document_number: company.document_number || '',
      state_registration: company.state_registration || '',
      municipal_registration: company.municipal_registration || '',
      foundation_date: company.foundation_date || '',
    };

    const toFieldMap = (items = []) => Object.fromEntries(
      (Array.isArray(items) ? items : [])
        .map((item) => [String(item?.name || '').trim(), item?.value])
        .filter(([name]) => Boolean(name))
    );

    const today = new Date();
    const foundationDateMax = [
      today.getFullYear(),
      String(today.getMonth() + 1).padStart(2, '0'),
      String(today.getDate()).padStart(2, '0'),
    ].join('-');

    await create({
      element: 'autoform',
      parent: 'main',
      id: 'company-profile-form',
      mode: 'edit',
      isDialog: true,
      autoOpen: true,
      kill: true,
      dirtyTracking: true,
      type: 'fieldset',
      columns: 2,
      title: this.#label('companyData'),
      data,
      dialog: {
        scope: 'workspace',
        variant: 'drawer',
        placement: 'right',
        effect: 'slide-right',
        backdrop: true,
        class: 'autoform-dialog company-profile-dialog',
        width: '56rem',
        maxWidth: 'calc(100vw - 1rem)',
        title: {
          icon: { type: 'solid', name: 'building' },
          text: this.#label('companyData'),
        },
      },
      groups: [
        {
          legend: this.#label('companyRegistration'),
          row: 0,
          col: 0,
          colspan: 2,
          isGrid: true,
          cols: 2,
          content: [
            {
              element: 'text',
              id: 'company_name',
              name: 'company_name',
              label: this.#label('companyName'),
              required: true,
              row: 0,
              col: 0,
              colspan: 2,
              full: true,
            },
            {
              element: 'text',
              id: 'trade_name',
              name: 'trade_name',
              label: this.#label('tradeName'),
              row: 1,
              col: 0,
              colspan: 2,
              full: true,
            },
            {
              element: 'geodoc',
              id: 'company-document',
              entity: 'organization',
              disabled: true,
              row: 2,
              col: 0,
              colspan: 2,
              values: {
                document_country: data.document_country,
                document_number: data.document_number,
              },
              fields: {
                country: {
                  name: 'document_country',
                  label: this.#label('country'),
                },
                type: {
                  label: this.#label('document'),
                },
                number: {
                  name: 'document_number',
                  label: this.#label('number'),
                },
              },
            },
            {
              element: 'text',
              id: 'state_registration',
              name: 'state_registration',
              label: this.#label('stateRegistration'),
              row: 3,
              col: 0,
              full: true,
            },
            {
              element: 'text',
              id: 'municipal_registration',
              name: 'municipal_registration',
              label: this.#label('municipalRegistration'),
              row: 3,
              col: 1,
              full: true,
            },
            {
              element: 'date',
              id: 'foundation_date',
              name: 'foundation_date',
              label: this.#label('foundationDate'),
              min: null,
              max: foundationDateMax,
              row: 4,
              col: 0,
            },
          ],
        },
      ],
      saveHandler: async ({ data: formData }) => {
        const values = toFieldMap(formData);
        const companyName = String(values.company_name || '').trim();

        if (!companyName) {
          return {
            status: 'error',
            code: 'COMPANY_NAME_REQUIRED',
            message: this.#label('companyNameRequired'),
          };
        }

        const updated = await authClient.callAuthMethod('updateCompanyProfile', {
          company: {
            company_name: companyName,
            trade_name: String(values.trade_name || '').trim() || null,
            state_registration: String(values.state_registration || '').trim() || null,
            municipal_registration: String(values.municipal_registration || '').trim() || null,
            foundation_date: String(values.foundation_date || '').trim() || null,
          },
        }, { loader: false });

        if (Number(updated?.status) !== 200 || !updated?.company) {
          return {
            status: 'error',
            code: updated?.error || 'COMPANY_PROFILE_UPDATE_FAILED',
          };
        }

        access.company = updated.company;
        return {
          status: 'success',
          rows: 1,
          data: [updated.company],
        };
      },
    });
  }

  async #openProfileDialog() {
    this.#closeUserMenu();

    let response;
    try {
      response = await authClient.callAuthMethod('getProfile', {}, {
        loader: false,
        retries: 1,
        retryDelay: 100,
      });
    } catch (error) {
      console.error('getProfile error:', error);
      msgbox({
        type: 'error',
        message: this.dic?.message?.errorOperation,
      });
      return;
    }

    const profile = response?.profile || {};
    const data = {
      full_name: profile.full_name || '',
      social_name: profile.social_name || '',
      nickname: profile.nickname || '',
      birth_date: profile.birth_date || '',
      gender_type: profile.gender_type || 'N',
      email: profile.email || '',
      photo_url: profile.photo_url || '',
      document_country: profile.document_country || '',
      document_type: profile.document_type || '',
      document_number: profile.document_number || '',
      phone: profile.phone || '',
      profile_address_zip: profile.profile_address_zip || '',
      profile_address_address: profile.profile_address_address || '',
      profile_address_number: profile.profile_address_number || '',
      profile_address_complement: profile.profile_address_complement || '',
      profile_address_neighborhood: profile.profile_address_neighborhood || '',
      profile_address_city: profile.profile_address_city || '',
      profile_address_state: profile.profile_address_state || '',
      profile_address_country: profile.profile_address_country || '',
    };

    const toFieldMap = (items = []) => Object.fromEntries(
      (Array.isArray(items) ? items : [])
        .map((item) => [String(item?.name || '').trim(), item?.value])
        .filter(([name]) => Boolean(name))
    );

    const normalizePhoneE164 = (value) => {
      const digits = String(value || '').replace(/\D/g, '');
      return digits ? `+${digits}` : null;
    };

    const buildProfileAddress = (values = {}) => {
      const address = {
        zip: String(values.profile_address_zip ?? '').trim(),
        address: String(values.profile_address_address ?? '').trim(),
        number: String(values.profile_address_number ?? '').trim() || null,
        complement: String(values.profile_address_complement ?? '').trim() || null,
        neighborhood: String(values.profile_address_neighborhood ?? '').trim() || null,
        city: String(values.profile_address_city ?? '').trim(),
        state: String(values.profile_address_state ?? '').trim() || null,
        country: String(values.profile_address_country ?? '').trim().toUpperCase(),
      };
      return Object.values(address).some((value) => Boolean(value)) ? address : null;
    };

    const toLocalIsoDate = (date) => [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0'),
    ].join('-');

    const birthDateMaxDate = new Date();
    const birthDateMinDate = new Date(birthDateMaxDate);
    const birthDateMonth = birthDateMaxDate.getMonth();
    birthDateMinDate.setFullYear(birthDateMaxDate.getFullYear() - 100);
    if (birthDateMinDate.getMonth() !== birthDateMonth) {
      birthDateMinDate.setDate(0);
    }
    const birthDateMin = toLocalIsoDate(birthDateMinDate);
    const birthDateMax = toLocalIsoDate(birthDateMaxDate);

    await create({
      element: 'autoform',
      parent: document.body,
      id: 'user-profile-form',
      mode: 'edit',
      isDialog: true,
      autoOpen: true,
      kill: true,
      dirtyTracking: true,
      type: 'fieldset',
      columns: 2,
      title: this.#label('myProfile'),
      data,
      dialog: {
        variant: 'drawer',
        placement: 'right',
        effect: 'slide-right',
        backdrop: true,
        class: 'autoform-dialog user-profile-dialog',
        width: '56rem',
        maxWidth: 'calc(100vw - 1rem)',
        title: {
          icon: { type: 'solid', name: 'user' },
          text: this.#label('myProfile'),
        },
      },
      groups: [
        {
          legend: this.#label('profileIdentification'),
          row: 0,
          col: 0,
          isGrid: true,
          cols: 2,
          content: [
            {
              element: 'upload',
              id: 'photo_url',
              name: 'photo_url',
              label: this.#label('profilePhoto'),
              text: this.#label('upload'),
              dialogTitle: this.#label('profilePhoto'),
              cancelText: this.#label('cancel'),
              submitText: this.#label('send'),
              accept: 'image/*',
              visibility: 'user-permanent',
              storeAs: 'string',
              crop: {
                format: 'circle',
                size: 512,
                outputType: 'image/jpeg',
              },
              onPersistedValueChange: (photoUrl) => {
                if (!photoUrl) return;
                // O avatar usa um nome estável (avatar.jpg). A versão evita
                // que o navegador mantenha em memória a imagem anterior.
                session.set('userPhotoURL', versionPrivateFileUrl(photoUrl));
                this.#refreshProfileHeader();
              },
              row: 0,
              col: 0,
              colspan: 2,
              full: true,
            },
            {
              element: 'text',
              id: 'full_name',
              name: 'full_name',
              label: this.#label('fullName'),
              row: 1,
              col: 0,
              colspan: 2,
              full: true,
            },
            {
              element: 'text',
              id: 'social_name',
              name: 'social_name',
              label: this.#label('socialName'),
              row: 2,
              col: 0,
              full: true,
            },
            {
              element: 'text',
              id: 'nickname',
              name: 'nickname',
              label: this.#label('nickname'),
              row: 2,
              col: 1,
              full: true,
            },
            {
              element: 'date',
              id: 'birth_date',
              name: 'birth_date',
              label: this.#label('birthDate'),
              min: birthDateMin,
              max: birthDateMax,
              row: 3,
              col: 0,
              full: true,
            },
            {
              element: 'combo',
              id: 'gender_type',
              name: 'gender_type',
              label: this.#label('gender'),
              row: 3,
              col: 1,
              full: true,
              options: [
                { value: 'M', label: this.#label('genderMale') },
                { value: 'F', label: this.#label('genderFemale') },
                { value: 'X', label: this.#label('genderNonBinary') },
                { value: 'N', label: this.#label('genderNotInformed') },
              ],
            },
            {
              element: 'geodoc',
              id: 'profile-document',
              entity: 'person',
              row: 4,
              col: 0,
              colspan: 2,
              values: {
                document_country: data.document_country,
                document_number: data.document_number,
              },
              fields: {
                country: {
                  name: 'document_country',
                  label: this.#label('country'),
                },
                type: {
                  label: this.#label('document'),
                },
                number: {
                  name: 'document_number',
                  label: this.#label('number'),
                },
              },
            },
          ],
        },
        {
          legend: this.#label('profileContacts'),
          row: 0,
          col: 1,
          isGrid: true,
          cols: 1,
          content: [
            {
              element: 'email',
              id: 'email',
              name: 'email',
              label: this.#label('profileEmail'),
              disabled: true,
              full: true,
            },
            {
              element: 'phone',
              id: 'phone',
              name: 'phone',
              label: this.#label('profilePhone'),
              value: data.phone,
              nationalMode: false,
              mask: {
                mode: 'MOBILE',
                national: false,
              },
            },
          ],
        },
        {
          legend: this.#label('profileAddress'),
          row: 1,
          col: 0,
          colspan: 2,
          isGrid: true,
          cols: 1,
          content: [
            {
              element: 'address',
              id: 'profile-address',
              name: 'profile_address',
              row: 0,
              col: 0,
              full: true,
              values: {
                profile_address_zip: data.profile_address_zip,
                profile_address_address: data.profile_address_address,
                profile_address_number: data.profile_address_number,
                profile_address_complement: data.profile_address_complement,
                profile_address_neighborhood: data.profile_address_neighborhood,
                profile_address_city: data.profile_address_city,
                profile_address_state: data.profile_address_state,
                profile_address_country: data.profile_address_country,
              },
              fields: {
                zip: { name: 'profile_address_zip' },
                address: { name: 'profile_address_address' },
                number: { name: 'profile_address_number' },
                complement: { name: 'profile_address_complement' },
                neighborhood: { name: 'profile_address_neighborhood' },
                city: { name: 'profile_address_city' },
                uf: { name: 'profile_address_state' },
                country: { name: 'profile_address_country' },
              },
            },
          ],
        },
      ],
      saveHandler: async ({ data: formData }) => {
        const values = toFieldMap(formData);
        const fullName = String(values.full_name || '').trim() || null;
        const socialName = String(values.social_name || '').trim() || null;
        const nickname = String(values.nickname || '').trim() || null;
        const phone = normalizePhoneE164(values.phone);
        const address = buildProfileAddress(values);

        if (!fullName && !socialName && !nickname) {
          return {
            status: 'error',
            code: 'PROFILE_DISPLAY_NAME_REQUIRED',
            message: this.#label('profileDisplayNameRequired'),
          };
        }

        if (phone && !/^\+[1-9]\d{6,14}$/.test(phone)) {
          return {
            status: 'error',
            code: 'PROFILE_PHONE_INVALID',
            message: this.#label('profilePhoneInvalid'),
          };
        }

        if (
          address
          && (!address.zip || !address.address || !address.city || !/^[A-Z]{2}$/.test(address.country))
        ) {
          return {
            status: 'error',
            code: 'PROFILE_ADDRESS_INCOMPLETE',
            message: this.#label('profileAddressIncomplete'),
          };
        }

        const updated = await authClient.callAuthMethod('updateProfile', {
          profile: {
            full_name: fullName,
            social_name: socialName,
            nickname,
            birth_date: String(values.birth_date || '').trim() || null,
            gender_type: String(values.gender_type || 'N').trim() || 'N',
            document_country: String(values.document_country || '').trim() || null,
            document_number: String(values.document_number || '').trim() || null,
            phone,
            address,
          },
        }, { loader: false });

        if (Number(updated?.status) !== 200 || !updated?.profile) {
          return { status: 'error', code: updated?.error || 'PROFILE_UPDATE_FAILED' };
        }

        const saved = updated.profile;
        const displayName = String(
          saved.nickname || saved.social_name || saved.full_name || ''
        ).trim();
        if (displayName) {
          session.set('user', displayName.split(/\s+/)[0]);
        }
        if (saved.photo_url) {
          session.set('userPhotoURL', versionPrivateFileUrl(saved.photo_url));
        }

        // O AutoForm salva o perfil sem recarregar a página. Atualize somente
        // o resumo do usuário: redesenhar o shell fecharia o conteúdo atual.
        this.#refreshProfileHeader();

        return {
          status: 'success',
          rows: 1,
          data: [saved],
        };
      },
    });
  }

  async #openPreferencesDialog() {
    this.#closeUserMenu();

    const currentProduct = inferProductFromHost(window.location?.hostname || '') || 'platform';
    let sessionData = null;
    try {
      sessionData = currentProduct && currentProduct !== 'site'
        ? await ajax.get({
          product: currentProduct,
          category: 'core',
          domain: 'core',
          module: 'master',
          method: 'getSessionData',
          config: { loader: false, retries: 1, retryDelay: 100 },
        })
        : await authClient.getSessionData({ loader: false, retries: 1, retryDelay: 100 });
    } catch (error) {
      console.warn('Falha ao carregar preferencias atuais:', error?.message || error);
    }

    const profile = sessionData?.profile || {};
    const stored = readVisualPreferences();
    const languageConfig = this.config?.language && typeof this.config.language === 'object'
      ? this.config.language
      : {};
    const defaultLanguage = String(languageConfig.default || 'pt-BR').trim() || 'pt-BR';
    const supportedLanguages = Array.from(new Set(
      (Array.isArray(languageConfig.supported) && languageConfig.supported.length
        ? languageConfig.supported
        : [defaultLanguage])
        .map((value) => String(value || '').trim())
        .filter(Boolean)
    ));
    const activeLanguage = String(
      profile.language
      || session.get('language')
      || defaultLanguage
    ).trim() || defaultLanguage;
    let languageNames = null;
    try {
      languageNames = new Intl.DisplayNames([activeLanguage], { type: 'language' });
    } catch {}

    const initial = {
      theme: normalizeVisualTheme(profile.theme || session.get('theme') || stored.theme),
      scale: normalizeVisualScale(profile.scale || session.get('scale') || stored.scale),
      language: supportedLanguages.includes(activeLanguage) ? activeLanguage : defaultLanguage,
      preferredMethod: ['email', 'authapp'].includes(
        String(profile.preferredMethod || profile.preferred_method || '').trim().toLowerCase()
      )
        ? String(profile.preferredMethod || profile.preferred_method).trim().toLowerCase()
        : 'email',
      notifEmail: profile.notifEmail ?? profile.notif_email ?? true,
      notifPush: profile.notifPush ?? profile.notif_push ?? false,
      startMode: ['dashboard', 'last_page', 'custom_page'].includes(
        String(profile.startMode || profile.start_mode || '').trim().toLowerCase()
      )
        ? String(profile.startMode || profile.start_mode).trim().toLowerCase()
        : 'last_page',
      startPage: profile.startPage || profile.start_page || null,
    };

    let preview = { ...initial };
    let committed = false;

    const restore = () => {
      if (!committed) {
        applyVisualPreferences({
          theme: initial.theme,
          scale: initial.scale,
        });
      }
    };

    const setPreference = (name, value, { visual = false } = {}) => {
      preview = { ...preview, [name]: value };
      if (visual) {
        applyVisualPreferences({
          theme: preview.theme,
          scale: preview.scale,
        });
      }
    };

    const inputGroupChange = (name, { visual = false } = {}) => (event) => {
      const target = event?.target;
      if (!target?.matches?.('input[type="radio"]') || !target.checked) return;
      setPreference(name, target.value, { visual });
    };

    const languageOptions = supportedLanguages.map((value) => {
      let label = value;
      try {
        label = languageNames?.of(value) || value;
      } catch {}
      return { value, label: `${label} (${value})` };
    });

    await create({
      element: 'div',
      parent: document.body,
      class: 'preferences-panel',
      dialog: {
        modal: true,
        variant: 'drawer',
        placement: 'right',
        effect: 'slide-right',
        backdrop: true,
        autoOpen: true,
        kill: true,
        class: 'preferences-dialog',
        onClose: restore,
        title: {
          icon: { type: 'solid', name: 'sliders' },
          text: this.#label('preferences', 'Preferências'),
        },
        buttons: [
          {
            text: this.#label('cancel', 'Cancelar'),
            color: 'red',
            icon: { type: 'solid', name: 'ban' },
            click: restore,
          },
          {
            text: this.#label('save', 'Salvar'),
            color: 'green',
            icon: { type: 'solid', name: 'floppy-disk' },
            data: { button: 'enter' },
            close: false,
            click: (event) => {
              const button = event.currentTarget;
              const dialog = button.closest('.dialog');
              (async () => {
                button.disabled = true;
                try {
                  const response = await ajax.post({
                    product: currentProduct,
                    category: 'security',
                    domain: 'identity',
                    module: 'auth',
                    method: 'updatePreferences',
                    body: {
                      theme: preview.theme,
                      scale: preview.scale,
                      language: preview.language,
                      preferredMethod: preview.preferredMethod,
                      startMode: preview.startMode,
                      startPage: preview.startPage,
                      notifEmail: preview.notifEmail,
                      notifPush: preview.notifPush,
                    },
                    config: { loader: false },
                  });

                  const saved = response?.preferences || preview;
                  preview = {
                    ...preview,
                    theme: normalizeVisualTheme(saved.theme ?? preview.theme),
                    scale: normalizeVisualScale(saved.scale ?? preview.scale),
                    language: String(saved.language ?? preview.language),
                    preferredMethod: String(saved.preferredMethod ?? saved.preferred_method ?? preview.preferredMethod),
                    startMode: String(saved.startMode ?? saved.start_mode ?? preview.startMode),
                    startPage: saved.startPage ?? saved.start_page ?? preview.startPage,
                    notifEmail: saved.notifEmail ?? saved.notif_email ?? preview.notifEmail,
                    notifPush: saved.notifPush ?? saved.notif_push ?? preview.notifPush,
                  };

                  committed = true;
                  session.set('theme', preview.theme);
                  session.set('scale', preview.scale);
                  session.set('language', preview.language);
                  session.set('preferredMethod', preview.preferredMethod);
                  session.set('startMode', preview.startMode);
                  session.set('startPage', preview.startPage);
                  session.set('notifEmail', preview.notifEmail);
                  session.set('notifPush', preview.notifPush);
                  applyVisualPreferences(
                    { theme: preview.theme, scale: preview.scale },
                    { persist: true }
                  );
                  dialog?.instance?.close?.('destroy');
                  toast({
                    type: 'success',
                    message: this.#label('preferencesSaved', 'Preferências salvas.'),
                  });

                  if (preview.language !== initial.language) {
                    setTimeout(() => window.location.reload(), 250);
                  }
                } catch (error) {
                  console.error('updatePreferences error:', error);
                  msgbox({
                    type: 'error',
                    message: this.dic?.message?.errorOperation || 'Não foi possível salvar as preferências.',
                  });
                } finally {
                  button.disabled = false;
                }
              })();
            },
          },
        ],
      },
      isGrid: true,
      content: [
        {
          element: 'group',
          type: 'div',
          cols: 1,
          groups: [
            {
              legend: this.#label('appearance'),
              cols: 1,
              content: [
                {
                  element: 'inputGroup',
                  id: 'preference-theme',
                  name: 'preference-theme',
                  label: this.#label('theme', 'Tema'),
                  type: 'radio',
                  appearance: 'button',
                  full: true,
                  cols: 3,
                  value: preview.theme,
                  options: [
                    {
                      value: 'light',
                      label: this.#label('themeLight', 'Claro'),
                      icon: { type: 'solid', name: 'sun' },
                    },
                    {
                      value: 'dark',
                      label: this.#label('themeDark', 'Escuro'),
                      icon: { type: 'solid', name: 'moon' },
                    },
                    {
                      value: 'system',
                      label: this.#label('themeSystem', 'Sistema'),
                      icon: { type: 'solid', name: 'desktop' },
                    },
                  ],
                  change: inputGroupChange('theme', { visual: true }),
                },
                {
                  element: 'inputGroup',
                  id: 'preference-scale',
                  name: 'preference-scale',
                  label: this.#label('interfaceScale', 'Escala da interface'),
                  type: 'radio',
                  appearance: 'button',
                  full: true,
                  cols: 3,
                  value: preview.scale,
                  options: [
                    { value: 'compact', label: this.#label('scaleCompact', 'Compacta') },
                    { value: 'default', label: this.#label('scaleDefault', 'Padrão') },
                    { value: 'confort', label: this.#label('scaleConfort', 'Confortável') },
                  ],
                  change: inputGroupChange('scale', { visual: true }),
                },
              ],
            },
            {
              legend: this.#label('general'),
              cols: 1,
              content: [
                {
                  element: 'combo',
                  id: 'preference-language',
                  name: 'preference-language',
                  label: this.#label('language'),
                  full: true,
                  filter: true,
                  options: languageOptions,
                  value: initial.language,
                  change: (event) => setPreference(
                    'language',
                    String(event?.detail?.nextValue ?? event?.target?.value ?? initial.language)
                  ),
                },
              ],
            },
            {
              legend: this.#label('security'),
              cols: 1,
              content: [
                {
                  element: 'inputGroup',
                  id: 'preference-preferred-method',
                  name: 'preference-preferred-method',
                  label: this.#label('preferredMfaMethod'),
                  type: 'radio',
                  appearance: 'button',
                  full: true,
                  cols: 2,
                  value: preview.preferredMethod,
                  options: [
                    { value: 'email', label: this.#label('mfaEmail') },
                    { value: 'authapp', label: this.#label('mfaAuthApp') },
                  ],
                  change: inputGroupChange('preferredMethod'),
                },
                {
                  tag: 'div',
                  class: 'preferences-field-help',
                  text: this.#label('preferredMfaMethodHelp'),
                },
              ],
            },
            {
              legend: this.#label('notifications'),
              cols: 1,
              content: [
                {
                  element: 'inputGroup',
                  id: ['preference-notif-email', 'preference-notif-push'],
                  name: 'preference-notifications',
                  type: 'checkbox',
                  appearance: 'switch',
                  full: true,
                  cols: 2,
                  value: [preview.notifEmail, preview.notifPush],
                  options: [
                    { label: this.#label('notificationsEmail') },
                    { label: this.#label('notificationsPush') },
                  ],
                  change: (event) => {
                    const target = event?.target;
                    if (!target?.matches?.('input[type="checkbox"]')) return;
                    if (target.id === 'preference-notif-email') {
                      setPreference('notifEmail', target.checked);
                    } else if (target.id === 'preference-notif-push') {
                      setPreference('notifPush', target.checked);
                    }
                  },
                },
              ],
            },
          ],
        },
      ],
    });
  }

  #closeUserMenu() {
    const userMenu = document.querySelector('.user-logged');
    const trigger = document.querySelector('.user-menu-trigger');

    userMenu?.classList.remove('open');
    trigger?.setAttribute('aria-expanded', 'false');
  }

  #setupUserMenu() {
    const userMenu = document.querySelector('.user-logged');
    const trigger = document.querySelector('.user-menu-trigger');
    if (!userMenu || !trigger) return;

    if (window.__userMenuPointerHandler) {
      window.removeEventListener('pointerdown', window.__userMenuPointerHandler, true);
    }

    if (window.__userMenuKeyHandler) {
      document.removeEventListener('keydown', window.__userMenuKeyHandler, true);
    }

    window.__userMenuPointerHandler = (event) => {
      const activeMenu = document.querySelector('.user-logged');
      if (activeMenu && !activeMenu.contains(event.target)) {
        this.#closeUserMenu();
      }
    };

    window.__userMenuKeyHandler = (event) => {
      if (event.key === 'Escape') {
        this.#closeUserMenu();
      }
    };

    window.addEventListener('pointerdown', window.__userMenuPointerHandler, true);
    document.addEventListener('keydown', window.__userMenuKeyHandler, true);
    this.#closeUserMenu();
  }

  async #notificationsCount() {
    const service = this.config?.logged?.notifications || this.config?.notifications || {};

    try {
      const response = await ajax.post({
        product: service.product || 'platform',
        category: service.category || 'communication',
        domain: service.domain || 'communication',
        module: service.module || 'notify',
        method: service.method || 'getNotificationsCount',
        body: {},
        config: { loader: false },
      });

      return Number(response?.count || 0);
    } catch (error) {
      console.warn('Falha ao carregar notificacoes:', error?.message || error);
      return 0;
    }
  }

  #brandContent() {
    const branding = this.dic?.branding || {};
    const content = [];
    const logoSource = branding.logoUrl || this.config.logoUrl || 'logo.png';
    const logoUrl = resolveKnownImageSource(logoSource, 'product');

    if (logoUrl) {
      content.push({
        element: 'image',
        origin: 'external',
        src: logoUrl,
        alt: this.config.logoAlt || branding.productName || 'Logo',
        hasFigure: false,
        class: 'header-logo'
      });
    }

    if (branding.title1 || branding.title2 || branding.subtitle) {
      content.push({
        element: 'div',
        class: 'brand-lockup',
        isGrid: true,
        content: [
          {
            element: 'div',
            class: 'brand-title',
            isGrid: true,
            content: [
              ...(branding.title1 ? [{ element: 'span', text: branding.title1 }] : []),
              ...(branding.title2 ? [{ element: 'span', class: 'yellow-p', text: branding.title2 }] : []),
            ]
          },
          ...(branding.subtitle ? [{ element: 'div', class: 'brand-subtitle', text: branding.subtitle }] : [])
        ]
      });
    }

    return content;
  }

  #footerContent() {
    const branding = this.dic?.branding || {};
    const footerTemplate = this.#format(
      branding.footer || '',
      {
        year: new Date().getFullYear(),
        company: '{company}',
      },
    );
    const footerCompany = branding?.footerCompany && typeof branding.footerCompany === 'object'
      ? branding.footerCompany
      : {};
    const companyName = String(footerCompany.name || '').trim();
    const companyUrl = String(footerCompany.url || '').trim();
    const companyToken = '{company}';
    const companyIndex = footerTemplate.indexOf(companyToken);

    if (companyIndex < 0 || !companyName || !companyUrl) {
      return [{
        element: 'span',
        html: this.#format(branding.footer || '', { year: new Date().getFullYear() }),
      }];
    }

    return [
      { element: 'span', text: footerTemplate.slice(0, companyIndex) },
      {
        element: 'a',
        class: 'footer-company-link',
        href: companyUrl,
        text: companyName,
        target: '_blank',
        rel: 'noopener noreferrer',
      },
      { element: 'span', text: footerTemplate.slice(companyIndex + companyToken.length) },
    ];
  }

  #clearShell() {
    document.querySelector('header')?.empty?.();
    document.querySelector('footer')?.empty?.();
    document.querySelector('aside')?.empty?.();
    document.querySelector('main')?.empty?.();
  }

  #isWebmanagerItem(item = {}) {
    return String(item?.category || '').trim() === 'webmanager'
      && String(item?.domain || '').trim() === 'sitemanager';
  }

  #hasWebmanagerItems(items = []) {
    return (Array.isArray(items) ? items : []).some((item) => (
      this.#isWebmanagerItem(item)
      || this.#hasWebmanagerItems(item?.items)
    ));
  }

  #filterWebmanagerMenu(items = [], disabledModules = []) {
    const disabled = new Set((Array.isArray(disabledModules) ? disabledModules : []).map(String));
    return (Array.isArray(items) ? items : [])
      .map((item) => {
        if (this.#isWebmanagerItem(item) && disabled.has(String(item?.module || ''))) return null;

        const children = Array.isArray(item?.items)
          ? this.#filterWebmanagerMenu(item.items, disabledModules)
          : null;
        if (Array.isArray(item?.items) && children.length === 0 && !this.#isWebmanagerItem(item)) {
          return null;
        }
        return Array.isArray(item?.items) ? { ...item, items: children } : item;
      })
      .filter(Boolean);
  }

  #findWebmanagerItemByPath(items = [], pathname = '') {
    for (const item of Array.isArray(items) ? items : []) {
      if (this.#isWebmanagerItem(item) && String(pathname).includes(`/${item.module}`)) return item;
      const nested = this.#findWebmanagerItemByPath(item?.items, pathname);
      if (nested) return nested;
    }
    return null;
  }

  async #getWebmanagerContext() {
    const currentProduct = inferProductFromHost(window.location?.hostname || '');
    const product = currentProduct || 'admindigital';
    try {
      return await ajax.post({
        product,
        category: 'webmanager',
        domain: 'sitemanager',
        module: 'context',
        method: 'getContext',
        body: {},
        config: { loader: false, retries: 1, retryDelay: 100 },
      });
    } catch (error) {
      console.warn('Falha ao carregar contexto de site:', error?.message || error);
      return null;
    }
  }

  async #setupWebmanagerSiteCombo(context, rawMenu, routeSlug) {
    if (!context || !Array.isArray(context.sites) || context.sites.length === 0) return;
    const aside = document.querySelector('aside');
    if (!aside) return;

    const wrap = document.createElement('div');
    wrap.className = 'webmanager-site-context';
    aside.prepend(wrap);

    const options = context.sites.map((site) => ({
      value: site.id,
      label: site.url,
    }));
    const combo = await createElement({
      element: 'combo',
      id: 'webmanager-active-site',
      name: 'webmanager-active-site',
      parent: wrap,
      hasFieldBox: false,
      label: false,
      full: true,
      filter: true,
      size: 10,
      options,
      value: context.activeSite?.id || '',
      placeholder: 'Selecione um site',
    });

    combo.addEventListener('change', async (event) => {
      const siteId = event?.detail?.nextValue || '';
      if (!siteId) return;
      const currentProduct = inferProductFromHost(window.location?.hostname || '');
      try {
        const nextContext = await ajax.post({
          product: currentProduct || 'admindigital',
          category: 'webmanager',
          domain: 'sitemanager',
          module: 'context',
          method: 'setActiveSite',
          body: { site_id: siteId },
          config: { loader: false },
        });
        const currentRouteSlug = this.#normalizeSlug(window.location.pathname || '/') || routeSlug;
        const currentItem = this.#findWebmanagerItemByPath(rawMenu, window.location.pathname || '');
        const disabled = new Set((nextContext?.disabledModules || []).map(String));
        const nextRoute = currentItem && disabled.has(String(currentItem.module || ''))
          ? null
          : currentRouteSlug;
        await this.#render(nextRoute, { menu: rawMenu, webmanagerContext: nextContext });
      } catch (error) {
        console.error('Falha ao trocar o site ativo:', error);
        toast({ type: 'error', message: 'Não foi possível trocar o site.' });
      }
    });
  }

  async #render(routeSlug = null, options = {}) {
    const notifications = await this.#notificationsCount();
    const rawMenu = Array.isArray(options?.menu)
      ? options.menu
      : await authClient.getMenu({ loader: false, forceRefresh: options?.forceRefreshMenu !== false });
    const currentProduct = inferProductFromHost(window.location?.hostname || '');
    const supportsWebmanagerContext = currentProduct === 'admindigital' || this.#hasWebmanagerItems(rawMenu);
    const webmanagerContext = supportsWebmanagerContext
      ? (options?.webmanagerContext || await this.#getWebmanagerContext())
      : null;
    const menu = webmanagerContext?.activeSite
      ? this.#filterWebmanagerMenu(rawMenu, webmanagerContext.disabledModules)
      : rawMenu;
    const userName = session.get('user') || '';
    const userPhotoURL = session.get('userPhotoURL');
    const companyAccess = await this.#getCompanyProfileAccess();

    this.#clearShell();

    await create([
      {
        element: 'a',
        parent: 'header',
        href: '/',
        class: 'brand-link',
        isGrid: true,
        content: this.#brandContent()
      },
      { element: 'div', parent: 'header' },
      {
        element: 'a',
        parent: 'header',
        class: 'm-not m-bell',
        icon: { type: 'solid', name: 'bell' },
        isGrid: true,
        content: [
          {
            element: 'span',
            class: 'm-not-qty',
            text: notifications > 99 ? '99+' : notifications.toString()
          },
        ]
      },
      {
        element: 'div',
        parent: 'header',
        class: 'user-logged',
        isGrid: true,
        content: [
          {
            element: 'a',
            class: 'user-menu-trigger',
            href: '#',
            aria: { haspopup: 'true', expanded: 'false' },
            click: (event) => {
              event.preventDefault();
              event.stopPropagation();
              const wrap = event.currentTarget.closest('.user-logged');
              const isOpen = wrap?.classList.contains('open');
              wrap?.classList.toggle('open', !isOpen);
              event.currentTarget.setAttribute('aria-expanded', (!isOpen).toString());
            },
            isGrid: true,
            content: [
              { element: 'span', text: this.#format(this.#label('hello', 'Ola, {name}!'), { name: userName }) },
              ...(userPhotoURL ? [{
                element: 'image',
                origin: 'external',
                src: userPhotoURL,
                alt: 'avatar',
                hasFigure: false,
                class: 'header-avatar'
              }] : []),
              { element: 'span', class: 'user-menu-arrow', aria: { hidden: 'true' }, text: '▼' }
            ]
          },
          {
            element: 'list',
            class: 'user-menu-dropdown',
            isGrid: true,
            content: [
              {
                element: 'item',
                isGrid: true,
                content: [{
                  element: 'a',
                  href: '#',
                  text: this.#label('myProfile'),
                  click: async (event) => {
                    event.preventDefault();
                    await this.#openProfileDialog();
                  },
                }],
              },
              ...(companyAccess.allowed ? [{
                element: 'item',
                isGrid: true,
                content: [{
                  element: 'a',
                  href: '#',
                  text: this.#label('companyData'),
                  click: async (event) => {
                    event.preventDefault();
                    await this.#openCompanyProfileDialog(companyAccess);
                  },
                }],
              }] : []),
              {
                element: 'item',
                isGrid: true,
                content: [{
                  element: 'a',
                  href: '#',
                  text: this.#label('preferences', 'Preferências'),
                  click: async (event) => {
                    event.preventDefault();
                    await this.#openPreferencesDialog();
                  },
                }],
              },
              {
                element: 'item',
                isGrid: true,
                content: [{
                  element: 'a',
                  href: '#',
                  text: this.#label('changePassword', 'Alterar Senha'),
                  click: async (event) => {
                    event.preventDefault();
                    await this.#openChangePasswordDialog();
                  }
                }]
              },
              {
                element: 'item',
                isGrid: true,
                content: [{
                  element: 'a',
                  href: '#',
                  text: this.#label('updateMenu', 'Atualizar menu'),
                  click: async (event) => {
                    event.preventDefault();
                    this.#closeUserMenu();
                    await this.#refreshMenu();
                  }
                }]
              },
              { element: 'item', class: 'separator' },
              {
                element: 'item',
                isGrid: true,
                content: [{
                  element: 'a',
                  href: '#',
                  text: this.#label('logout', 'Sair'),
                  click: async (event) => {
                    event.preventDefault();
                    await this.#logout();
                  }
                }]
              }
            ]
          }
        ]
      },
      {
        element: 'div',
        parent: 'header',
        class: 'user-menu-backdrop',
        click: () => this.#closeUserMenu()
      },
      { element: 'div', parent: 'footer', content: this.#footerContent() },
      { element: 'div', parent: 'footer' },
      {
        element: 'menu',
        parent: 'aside',
        items: menu,
        type: 'aside',
        dics: DEFAULT_MENU_DICS,
        route: {
          slug: routeSlug,
          onNavigate: ({ slug, label: itemLabel, source }) => {
            this.#updateUrl(source === 'route' ? slug : null, { replace: source === 'route' });
            this.#updateTitle(itemLabel || slug);
            this.#trackPageView();
          },
          onRouteMiss: ({ slug }) => {
            toast({
              type: 'warning',
              message: this.dic?.message?.slugNotFound || `Modulo nao encontrado para o slug "${slug}".`
            });
            this.#updateUrl(null, { replace: true });
          }
        }
      }
    ]);

    this.#setupUserMenu();
    if (supportsWebmanagerContext) {
      await this.#setupWebmanagerSiteCombo(webmanagerContext, rawMenu, routeSlug);
    }
  }
}

export default async function initLoggedShell() {
  const loggedShell = new LoggedShellInit();
  await loggedShell.run();
}
